import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl } from '../../utils/helpers';
import { Plus } from 'lucide-react';
import { 
  InstagramIcon, 
  TwitterIcon, 
  FacebookIcon, 
  TikTokIcon, 
  SnapchatIcon, 
  LinkedInIcon, 
  YouTubeIcon 
} from '../icons/SocialMediaIcons';

const ClientsListPage = () => {
  const { user, isAdmin } = useAuth();
  const { isRTL, t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await clientsAPI.getAll();
      // Parse social_media_accounts if it's a string
      const clients = response.data.clients.map(client => {
        let socialMediaAccounts = client.social_media_accounts;
        if (typeof socialMediaAccounts === 'string') {
          try {
            socialMediaAccounts = JSON.parse(socialMediaAccounts);
          } catch (e) {
            socialMediaAccounts = null;
          }
        }
        return {
          ...client,
          social_media_accounts: socialMediaAccounts
        };
      });
      setClients(clients);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${isRTL ? 'text-right' : 'text-left'}`}>
        <p className="text-rose-600 mb-4">{error}</p>
        <Button onClick={fetchClients} variant="secondary">
          {t('tryAgain')}
        </Button>
      </div>
    );
  }

  const totalActive = clients.filter((c) => c.is_active !== false).length;
  const totalInactive = clients.filter((c) => c.is_active === false).length;

  const filteredClients =
    statusFilter === 'all'
      ? clients
      : clients.filter((c) =>
          statusFilter === 'active' ? c.is_active !== false : c.is_active === false
        );

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 ${
            'sm:justify-between'
          }`}
        >
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h2 className="text-2xl font-bold text-slate-900">
              {t('clientsTitle')}
            </h2>
            <p className="text-slate-600 text-sm mt-1">{t('clientsSubtitle')}</p>
          </div>
          {isAdmin && (
            <Link to="/clients/new" className={isRTL ? 'mr-auto sm:mr-0' : 'ml-auto sm:ml-0'}>
              <Button
                variant="primary"
                className={`bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30 ${
                  ''
                }`}
              >
                <Plus className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('addNewClient')}
              </Button>
            </Link>
          )}
        </div>

        {/* Summary badges */}
        <div
          className={`mt-4 flex flex-wrap gap-3 text-xs ${
            ''
          }`}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700">
            👥 {t('allClientsFilter') || t('all')} {clients.length}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">
            ✅ {t('active')} {totalActive}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-100 text-rose-700">
            ⏸ {t('inactive')} {totalInactive}
          </span>
        </div>
      </Card>

      {/* Status Filter Pills */}
      <div
        className={`flex flex-wrap gap-2 ${
          isRTL ? 'justify-end' : 'justify-start'
        }`}
      >
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('allClientsFilter') || t('all')} ({clients.length})
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'active'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('active')} ({totalActive})
        </button>
        <button
          onClick={() => setStatusFilter('inactive')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'inactive'
              ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('inactive')} ({totalInactive})
        </button>
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card className="p-12">
          <div className="text-center" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('noClientsHeader')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('noClientsDescription')}
            </p>
            {isAdmin && (
              <Link to="/clients/new">
                <Button variant="primary">
                  {t('addFirstClient')}
                </Button>
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <Link key={client.id} to={`/clients/${client.id}`}>
              <Card hover className="h-full p-6 relative">
                {/* Status Badge */}
                {client.is_active !== undefined && (
                  <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      client.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {client.is_active ? t('active') : t('inactive')}
                    </span>
                  </div>
                )}

                {/* Client Info */}
                <div className="flex items-start gap-4 mb-4">
                  {/* Logo */}
                  {client.logo_url && (
                    <div className="flex-shrink-0">
                      <img
                        src={getImageUrl(client.logo_url)}
                        alt={client.company_name}
                        className="w-16 h-16 object-contain rounded-lg border-2 border-gray-200 bg-white"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
                      {client.company_name}
                    </h3>
                    {client.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {client.description}
                      </p>
                    )}

                    {/* Store Platform Badge */}
                    {client.social_media_accounts &&
                      typeof client.social_media_accounts === 'object' &&
                      client.social_media_accounts.store_platform && (
                        <div className="mt-2 flex items-center gap-2">
                          {client.social_media_accounts.store_platform === 'zid' && (
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 border border-purple-200">
                                <img
                                  src="/assets/logos/zid.png"
                                  alt="Zid"
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </span>
                              <span className="text-xs font-medium text-purple-700">
                                {isRTL ? 'متجر زد' : 'Zid Store'}
                              </span>
                            </div>
                          )}
                          {client.social_media_accounts.store_platform === '3tech' && (
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-50 border border-indigo-200">
                                <img
                                  src="/assets/logos/3tech.png"
                                  alt="3Tech"
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </span>
                              <span className="text-xs font-medium text-indigo-700">
                                {isRTL ? 'متجر 3تك' : '3Tech Store'}
                              </span>
                            </div>
                          )}
                          {client.social_media_accounts.store_platform === 'salla' && (
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-50 border border-teal-200">
                                <img
                                  src="/assets/logos/salla.png"
                                  alt="Salla"
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </span>
                              <span className="text-xs font-medium text-teal-700">
                                {isRTL ? 'متجر سلة' : 'Salla Store'}
                              </span>
                            </div>
                          )}
                          {client.social_media_accounts.store_platform === 'other' && (
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-50 border border-slate-200">
                                <span className="text-xs text-slate-500">⋯</span>
                              </span>
                              <span className="text-xs font-medium text-slate-600">
                                {isRTL ? 'منصة أخرى' : 'Other Platform'}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>

                {/* Package Info */}
                <div className="space-y-2 text-sm pt-4 border-t border-gray-100" dir={isRTL ? 'rtl' : 'ltr'}>
                  {client.active_packages_count > 0 ? (
                    <>
                      <div className="flex items-center justify-start gap-2">
                        <span className="text-gray-500" style={{ 
                          minWidth: isRTL ? '60px' : '80px',
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          {isRTL ? 'الباقات:' : 'Packages:'}
                        </span>
                        <span className="font-medium text-gray-900" dir="rtl" style={{ 
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          {isRTL ? `${client.active_packages_count} نشطة` : `${client.active_packages_count} active`}
                        </span>
                      </div>
                      <div className="flex items-center justify-start gap-2">
                        <span className="text-gray-500" style={{ 
                          minWidth: isRTL ? '60px' : '80px',
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          {isRTL ? 'متبقي:' : 'Remaining:'}
                        </span>
                        <span className={`font-medium ${
                          client.total_posts_remaining === 0 
                            ? 'text-red-600' 
                            : client.total_posts_remaining <= 3 
                              ? 'text-yellow-600' 
                              : 'text-green-600'
                        }`} dir={isRTL ? 'rtl' : 'ltr'} style={{ 
                          textAlign: isRTL ? 'right' : 'left'
                        }}>
                          {isRTL ? `${client.total_posts_remaining} بوست` : `${client.total_posts_remaining} posts`}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500" dir={isRTL ? 'rtl' : 'ltr'} style={{ 
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {isRTL ? 'لا توجد باقات نشطة' : 'No active packages'}
                    </div>
                  )}
                  <div className="flex items-center justify-start gap-2">
                    <span className="text-gray-500" style={{ 
                      minWidth: isRTL ? '60px' : '80px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {t('createdLabel')}
                    </span>
                    <span className="font-medium text-gray-900" dir={isRTL ? 'rtl' : 'ltr'} style={{ 
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {formatDate(client.created_at)}
                    </span>
                  </div>
                </div>

                {/* Social Media Accounts */}
                {client.social_media_accounts && typeof client.social_media_accounts === 'object' && Object.keys(client.social_media_accounts).length > 0 && (
                  <div className="pt-3 mt-3 border-t border-gray-100">
                    <p className={`text-xs text-gray-500 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('socialMediaLabel')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {client.social_media_accounts.instagram && (
                        <a
                          href={`https://instagram.com/${client.social_media_accounts.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors"
                          title={client.social_media_accounts.instagram}
                        >
                          <InstagramIcon className="w-4 h-4" />
                        </a>
                      )}
                      {client.social_media_accounts.twitter && (
                        <a
                          href={`https://twitter.com/${client.social_media_accounts.twitter.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors"
                          title={client.social_media_accounts.twitter}
                        >
                          <TwitterIcon className="w-4 h-4" />
                        </a>
                      )}
                      {client.social_media_accounts.facebook && (
                        <a
                          href={`https://facebook.com/${client.social_media_accounts.facebook}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                          title={client.social_media_accounts.facebook}
                        >
                          <FacebookIcon className="w-4 h-4" />
                        </a>
                      )}
                      {client.social_media_accounts.tiktok && (
                        <a
                          href={`https://tiktok.com/@${client.social_media_accounts.tiktok.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors"
                          title={client.social_media_accounts.tiktok}
                        >
                          <TikTokIcon className="w-4 h-4" />
                        </a>
                      )}
                      {client.social_media_accounts.snapchat && (
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 bg-yellow-50 border border-yellow-200 rounded-full"
                          title={client.social_media_accounts.snapchat}
                        >
                          <SnapchatIcon className="w-4 h-4" />
                        </span>
                      )}
                      {client.social_media_accounts.linkedin && (
                        <a
                          href={`https://linkedin.com/company/${client.social_media_accounts.linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                          title={client.social_media_accounts.linkedin}
                        >
                          <LinkedInIcon className="w-4 h-4" />
                        </a>
                      )}
                      {client.social_media_accounts.youtube && (
                        <a
                          href={`https://youtube.com/@${client.social_media_accounts.youtube.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 bg-red-50 rounded-full hover:bg-red-100 transition-colors"
                          title={client.social_media_accounts.youtube}
                        >
                          <YouTubeIcon className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsListPage;


